import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { ignoreNil, TelemetryService } from "@obg/ngx.global";
import { RouteSelector } from "@obg/ngx.router";
import { combineLatest } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { AppError } from "../../app-error/app-error.model";
import { AUTH_MESSAGE_CODES, AUTH_WITH_CREDENTIALS_FAIL_ACTIONS } from "../../auth/auth.const";
import { SESSION_ACTION_TYPE } from "../../auth/session.action";
import { AUTH_TRANSACTION_ACTION_TYPE } from "../../auth-transaction/auth-transaction.action";
import { DGA_AUTH_TRANSACTION_ACTION_TYPE } from "../../auth-transaction/dga/dga-auth-transaction.action";
import { GOOGLE_AUTH_TRANSACTION_ACTION_TYPE } from "../../auth-transaction/google/google-auth-transaction.action";
import { CustomerManagementSelector } from "../../customer-management/customer-management.selector";
import { Jurisdiction } from "../../jurisdiction/jurisdiction.model";
import { JurisdictionSelector } from "../../jurisdiction/jurisdiction.selector";
import { getMessageCodeFromError } from "../../message/message.util";
import { CORE_ROUTE } from "../../route";
import { CoreState } from "../../state";
import { WalletSelector } from "../../wallet/wallet.selector";
import { TRACKING_GTM_STATUSES } from "../tracking.const";

import { AuthTelemetryData, AuthTelemetryEventName, LoginMethod, LoginStatus, LoginType } from "./auth-telemetry.model";

@Injectable()
export class AuthTelemetryEffect {
	loginSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					SESSION_ACTION_TYPE.loginSuccess,
					SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess,
					SESSION_ACTION_TYPE.loginWithVerificationCode,
					SESSION_ACTION_TYPE.loginWithTokenSuccess
				),
				map((x: ActionWithPayload) => x),
				switchMap((action) =>
					combineLatest([
						this.store.select(JurisdictionSelector.get).pipe(ignoreNil()),
						this.store.select(CustomerManagementSelector.getKYCStatus).pipe(ignoreNil()),
						this.store.select(this.walletSelector.getBalance()).pipe(ignoreNil()),
						this.store.select(CustomerManagementSelector.getCountryOfRegistration).pipe(ignoreNil()),
						this.store.select(CustomerManagementSelector.getCustomerId).pipe(ignoreNil()),
					]).pipe(
						take(1),
						tap(([jurisdiction, kyc, balance, countryOfRegistration, customerId]) => {
							if (action.type === SESSION_ACTION_TYPE.loginWithTokenSuccess) {
								this.trackLoginSuccess({
									response: action.payload,
									loginMethod:
										action.payload.authenticationMethod ||
										this.loginMethodByJurisdiction(jurisdiction),
									kyc,
									balance,
									countryOfRegistration,
								});
								return;
							}
							if (action.type === SESSION_ACTION_TYPE.loginWithVerificationCode) {
								action.payload.customerId = customerId;
								this.trackLoginSuccess({
									response: action.payload,
									loginMethod: action.payload.authenticationMethod || LoginMethod.Credentials,
									kyc,
									balance,
									countryOfRegistration,
								});
								return;
							}
							this.trackLoginSuccess({
								response: action.payload,
								loginMethod: action.payload.authenticationMethod || LoginMethod.Credentials,
								kyc,
								balance,
								countryOfRegistration,
							});
						})
					)
				)
			),
		{ dispatch: false }
	);

	login$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SESSION_ACTION_TYPE.login),
				concatLatestFrom(() => this.store.select(RouteSelector.getCurrent)),
				filter(([, current]) => current?.name !== CORE_ROUTE.registration.register),
				tap(() => {
					this.service.track({
						event: AuthTelemetryEventName.LoginSubmitted,
						LoginMethod: LoginMethod.Credentials,
					});
				})
			),
		{ dispatch: false }
	);

	loginFail$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType<ActionWithPayload<AppError | Error>>(
					AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail,
					AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail,
					AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationFail,
					DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail,
					DGA_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail,
					GOOGLE_AUTH_TRANSACTION_ACTION_TYPE.setOpenIdConnectUrlFail,
					SESSION_ACTION_TYPE.loginWithTokenFail,
					...AUTH_WITH_CREDENTIALS_FAIL_ACTIONS
				),
				map((x: ActionWithPayload) => x),
				filter(
					(action) =>
						![
							AUTH_MESSAGE_CODES.login.consentsNotAccepted,
							AUTH_MESSAGE_CODES.login.termsAndConditionsNotAccepted,
							AUTH_MESSAGE_CODES.login.latestTermsAndConditionsNotAccepted,
							AUTH_MESSAGE_CODES.login.customerNotVerified,
							AUTH_MESSAGE_CODES.login.latestPrivacyPolicyNotAccepted,
							AUTH_MESSAGE_CODES.login.latestEntityLegalNoticeNotAccepted,
						].includes(getMessageCodeFromError(action.payload))
				),
				concatLatestFrom(() => this.store.select(JurisdictionSelector.get)),
				tap(([action, jurisdiction]) => {
					if (AUTH_WITH_CREDENTIALS_FAIL_ACTIONS.includes(action.type)) {
						this.trackLoginFail(action.payload, LoginMethod.Credentials);
						return;
					}
					this.trackLoginFail(
						action.payload,
						action.payload.authenticationMethod || this.loginMethodByJurisdiction(jurisdiction)
					);
				})
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private service: TelemetryService,
		private store: Store<CoreState>,
		private walletSelector: WalletSelector
	) {}

	private trackLoginFail(error: AppError | Error, loginMethod: LoginMethod) {
		this.service.track({
			event: AuthTelemetryEventName.LoginFailed,
			Error: getMessageCodeFromError(error),
			LoginMethod: loginMethod,
		});
	}

	private trackLoginSuccess(telemetry: AuthTelemetryData) {
		this.service.track({
			event: AuthTelemetryEventName.SuccessfulLogin,
			KYCStatus: `${TRACKING_GTM_STATUSES.KYC_STATUS} ${telemetry.kyc}`,
			GUID: telemetry.response.customerId,
			HashedEmail: telemetry.response.hashedEmail || "Missing",
			LoginStatus: LoginStatus.LoggedIn,
			LoginType: telemetry.response.isFirstLogin ? LoginType.FirstLogin : LoginType.LoggedInExisting,
			LoginMethod: telemetry.loginMethod,
			BalanceAmount: telemetry.balance.totalAmount,
			Currency: telemetry.balance.currencyCode,
			RegistrationCountry: telemetry.countryOfRegistration,
		});
	}

	private loginMethodByJurisdiction(jurisdiction: Jurisdiction | undefined): LoginMethod {
		switch (jurisdiction) {
			case Jurisdiction.Dga:
				return LoginMethod.MitId;
			case Jurisdiction.Mta:
			case Jurisdiction.Iaui:
				return LoginMethod.SmartId;
			default:
				return LoginMethod.BankId;
		}
	}
}
